/**
 * StatusRecentUpdates.tsx
 * ----------------
 * Renders a list of the most recent updates to service status.
 */

import { IconButton } from '@fluentui/react/lib/Button';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { useId } from '@fluentui/react-hooks';
import { List, Map } from 'immutable';
import * as React from 'react';

import { Service } from '../../models/service';
import {
    LiveSiteEventLogType,
    LiveSiteEventSeverity,
    LiveSiteEventState,
    RecentUpdate,
} from '../../models/status';
import { CollectionEmptyState } from '../shared/CollectionEmptyState';
import { ChatIcon, FluentSvgIcon } from '../shared/FluentSvgIcon';
import { StatusIndicator } from '../shared/StatusIndicator';
import {
    getTitleForGeography,
    isAllGeographiesCollection,
} from '../../utilities/geographies';
import { formatUtcAsLocalInEnglishLocale } from '../../utilities/time';

/**
 * Interfaces
 */

interface StatusRecentUpdatesRowProps {
    description: string;
    geographies?: string[];
    index: number;
    serviceName: string;
    severity?: LiveSiteEventSeverity;
    state: LiveSiteEventState;
    timestampUtc: string;
    timestampFormatter?: (timestamp: string) => string;
    updateType: LiveSiteEventLogType;
}

interface StatusRecentUpdatesProps {
    recentUpdates: List<RecentUpdate>;
    services: Map<string, Service>;
}

/**
 * Private variables and functions
 */

function getGeographiesText(geographies: string[] | undefined) {
    if (geographies === undefined || geographies === null) {
        return 'N/A';
    }

    // If all known geographies are submitted, output All Geographies
    if (isAllGeographiesCollection(geographies)) {
        return 'All geographies';
    }

    return geographies
        .map((geo) => getTitleForGeography(geo))
        .sort()
        .join(', ');
}

/**
 * Private components
 */

// TODO: this is only exported for testing purposes. In future, convert this to a general purpose list component.
export const StatusRecentUpdatesRow = (props: StatusRecentUpdatesRowProps) => {
    const {
        description,
        geographies,
        index,
        serviceName,
        severity,
        state,
        timestampUtc,
        timestampFormatter,
        updateType,
    } = props;
    const descriptionId = useId('status-recent-updates-row__description');

    // Component state hooks
    const [isExpanded, setIsExpanded] = React.useState<boolean>(false);

    // ID hooks
    const commentTooltipId = useId('status-recent-updates-row__comment-icon');

    // Callback hooks
    const onIsExpandedClicked = React.useCallback(() => {
        setIsExpanded(!isExpanded);
    }, [isExpanded, setIsExpanded]);

    const caretIconName = isExpanded ? 'ChevronUpMed' : 'ChevronRightMed';
    const caretTitle = isExpanded
        ? `Hide description for recent update number ${index + 1}`
        : `Show description for recent update number ${index + 1}`;
    const formattedTimestamp =
        timestampFormatter === undefined
            ? formatUtcAsLocalInEnglishLocale(timestampUtc)
            : timestampFormatter(timestampUtc);

    // Details node needs to exist to have focus when isExpanded is clicked, which is why we use visibility instead of
    // conditional rendering.
    const detailsProps = isExpanded
        ? {}
        : { style: { display: 'none' as 'none' } };

    return (
        <div className="status-recent-updates-row" role="rowgroup">
            <div className="status-recent-updates-row__cells" role="row">
                <div
                    className="status-recent-updates-row__cell status-recent-updates-row__cell--service"
                    role="cell"
                >
                    <span>
                        <IconButton
                            ariaLabel={caretTitle}
                            className="status-recent-updates-row__caret"
                            iconProps={{ iconName: caretIconName }}
                            onClick={onIsExpandedClicked}
                            onRenderIcon={(props) => {
                                const iconName =
                                    props !== undefined &&
                                    props.iconProps !== undefined
                                        ? props.iconProps.iconName
                                        : undefined;
                                const className =
                                    iconName === 'ChevronUpMed'
                                        ? 'status-recent-updates-row__caret-icon status-recent-updates-row__caret-icon--expanded'
                                        : 'status-recent-updates-row__caret-icon';

                                return (
                                    <FluentSvgIcon
                                        className={className}
                                        iconName={
                                            iconName !== undefined
                                                ? iconName
                                                : ''
                                        }
                                    />
                                );
                            }}
                            title={caretTitle}
                        />
                    </span>
                    <span>{serviceName}</span>
                </div>

                <div
                    className="status-recent-updates-row__cell status-recent-updates-row__cell--status"
                    role="cell"
                >
                    <div className="status-recent-updates-row__status-icon">
                        {updateType === 'Comment' ? (
                            <TooltipHost
                                calloutProps={{ gapSpace: 0 }}
                                content="Comment"
                                id={commentTooltipId}
                                styles={{ root: { display: 'inline-block' } }}
                            >
                                <div
                                    aria-describedby={commentTooltipId}
                                    aria-label="Comment"
                                    aria-labelledby={commentTooltipId}
                                    role="img"
                                >
                                    <ChatIcon
                                        className="status-recent-updates-row__comment-icon"
                                        width={20}
                                    />
                                </div>
                            </TooltipHost>
                        ) : (
                            <StatusIndicator
                                severity={severity}
                                state={state}
                            />
                        )}
                    </div>
                </div>

                <div
                    className="status-recent-updates-row__cell status-recent-updates-row__cell--timestamp"
                    role="cell"
                >
                    {formattedTimestamp}
                </div>

                <div
                    className="status-recent-updates-row__cell status-recent-updates-row__cell--geographies"
                    role="cell"
                >
                    {getGeographiesText(geographies)}
                </div>
            </div>

            <div
                aria-labelledby={descriptionId}
                className="status-recent-updates-row__details"
                role="row"
                {...detailsProps}
            >
                <span role="cell">
                    <div
                        className="status-recent-updates-row__description"
                        id={descriptionId}
                        {...detailsProps}
                    >
                        {description}
                    </div>
                </span>
            </div>
        </div>
    );
};

/**
 * Display all the recent updates to service statuses
 */
export function StatusRecentUpdates(props: StatusRecentUpdatesProps) {
    const { recentUpdates, services } = props;
    const descriptionId = useId('status-recent-updates__description');

    return (
        <div
            aria-describedby={descriptionId}
            aria-label="Recent Updates"
            className="status-recent-updates__container"
            role="table"
        >
            <div
                className="status-recent-updates__description"
                id={descriptionId}
            >
                A list of the ten most recent updates made to the status of
                Visual Studio services.
            </div>

            <div className="status-recent-updates-header" role="rowgroup">
                <div className="status-recent-updates-header__cells" role="row">
                    <div
                        className="status-recent-updates-header__cell status-recent-updates-header__cell--service"
                        role="columnheader"
                    >
                        Service
                    </div>

                    <div
                        className="status-recent-updates-header__cell status-recent-updates-header__cell--status"
                        role="columnheader"
                    >
                        Status
                    </div>

                    <div
                        className="status-recent-updates-header__cell status-recent-updates-header__cell--timestamp"
                        role="columnheader"
                    >
                        Date &amp; Time
                    </div>

                    <div
                        className="status-recent-updates-header__cell status-recent-updates-header__cell--geographies"
                        role="columnheader"
                    >
                        Geographies
                    </div>
                </div>
            </div>

            {recentUpdates
                .map((recentUpdate, i) => {
                    const {
                        description,
                        geographies,
                        service,
                        severity,
                        state,
                        timestampUtc: timestamp,
                        updateType,
                    } = recentUpdate;

                    if (!services.has(service)) {
                        throw new Error(
                            `Could not find service with ID ${service}.`
                        );
                    }

                    return (
                        <StatusRecentUpdatesRow
                            description={description}
                            geographies={geographies}
                            index={i}
                            key={`status-recent-update-row-${i}`}
                            serviceName={
                                (services.get(service) as Service).name
                            }
                            severity={severity}
                            state={state}
                            timestampUtc={timestamp}
                            updateType={updateType}
                        />
                    );
                })
                .toArray()}

            {recentUpdates.isEmpty() && (
                <div className="status-recent-updates__empty-state">
                    <CollectionEmptyState message="No status updates yet." />
                </div>
            )}

            {!recentUpdates.isEmpty() && (
                <div className="status-recent-updates__last-n-notice">
                    {recentUpdates.count() > 1
                        ? `Last ${recentUpdates.count()} updates`
                        : 'Last update'}
                </div>
            )}
        </div>
    );
}
